import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { GetSessionError } from '~/backend/connector-api/connector-components';
import {
    ContouringCustomizationFile,
    UserCustomization,
} from '~/backend/types';
import ImageAndStructureSetDisplay from '~/components/InputDataPreview/ImageAndStructureSetDisplay';
import ImageDataDisplay from '~/components/InputDataPreview/ImageDataDisplay';
import AutoSegmentationModelSelection from '~/containers/workflows/contour/AutoSegmentation/AutoSegmentationModelSelection';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';

type AutoSegmentationProps = {
    availableModels: PredictionModel[];
    modelCustomizations: UserCustomization<ContouringCustomizationFile>[];
    onSubmit: (
        model: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ) => void;
    isLoadingModels: boolean;
    isLoadingSession?: boolean;
    isSubmitting?: boolean;
    error: Error | GetSessionError | null;
    showPseudonymization: boolean;
};

export default function AutoSegmentation({
    availableModels,
    modelCustomizations,
    onSubmit,
    isLoadingModels,
    isLoadingSession,
    isSubmitting,
    error,
    showPseudonymization,
}: AutoSegmentationProps) {
    const primaryImage = useSelectedPrimaryImage();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    <FormattedMessage id="runAutomaticSegmentation" />
                </Typography>
                <Typography variant="body2">
                    <FormattedMessage id="runAutomaticSegmentationDescription" />
                </Typography>
            </Box>
            <Box display="grid" gap={2}>
                {currentStructureSet ? (
                    <ImageAndStructureSetDisplay
                        image={primaryImage}
                        structureSet={currentStructureSet}
                        isLoading={isLoadingSession}
                        error={error}
                        optional
                    />
                ) : (
                    <ImageDataDisplay
                        image={primaryImage}
                        isLoading={isLoadingSession}
                        error={error}
                    />
                )}
                <AutoSegmentationModelSelection
                    isLoadingModels={isLoadingModels}
                    availableModels={availableModels}
                    modelCustomizations={
                        modelCustomizations as unknown as UserCustomization<ContouringCustomizationFile>[]
                    }
                    onSubmit={onSubmit}
                    modality={primaryImage?.seriesMetadata.modality}
                    showPseudonymization={showPseudonymization}
                    disabled={isLoadingModels}
                    isSubmitting={isSubmitting}
                />
            </Box>
        </Box>
    );
}
